import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BookOpen,
  Calendar,
  Clock,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Maximize2,
  Minimize2,
  PlayCircle,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";

const LearningMaterialsPage = () => {
  const [recordings, setRecordings] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activeTab, setActiveTab] = useState("notes");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedNote, setSelectedNote] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [recordingsRes, notesRes] = await Promise.all([
          axios.get(`${API}/recordings`, { withCredentials: true }),
          axios.get(`${API}/notes`, { withCredentials: true }),
        ]);
        setRecordings(asArray(recordingsRes.data, "recordings"));
        setNotes(asArray(notesRes.data, "notes"));
      } catch (error) {
        console.error("Error fetching learning materials:", error); // eslint-disable-line no-console
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const filteredRecordings = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return recordings;
    return recordings.filter(
      (item) =>
        `${item.title || ""} ${item.description || ""}`.toLowerCase().includes(q)
    );
  }, [recordings, query]);

  const filteredNotes = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter(
      (item) =>
        `${item.title || ""} ${item.description || ""}`.toLowerCase().includes(q)
    );
  }, [notes, query]);

  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    const videoId = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|embed\/)([^&\s/]+)/
    )?.[1];
    return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
  };

  const downloadNote = async (note) => {
    try {
      await axios.post(`${API}/notes/${note.note_id}/download`, {}, { withCredentials: true });
    } catch {
      // count ignore
    }
    if (note.file_url) {
      window.open(note.file_url, "_blank", "noopener,noreferrer");
      toast.success("Downloading document...");
    }
  };

  const getViewerUrl = (url) => {
    if (!url) return "";
    if (url.includes("drive.google.com/file/d/")) {
      return url.replace(/\/view(\?.*)?$/, "/preview");
    }
    if (url.includes("docs.google.com") && !url.includes("/preview")) {
      return url.replace(/\/edit(\?.*)?$/, "/preview");
    }
    return url;
  };

  const isImageFile = (url) => {
    if (!url) return false;
    return /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(url);
  };

  if (loading) {
    return (
      <DashboardLayout title="Learning Materials">
        <div className="flex h-64 items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#00FF88] border-t-transparent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Learning Materials">
      <div className="space-y-5" data-testid="learning-materials-page">
        {/* Header Banner */}
        <div className="terminal-card relative overflow-hidden rounded-xl p-5 border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-[#0B0D10]">
          <div className="absolute right-0 top-0 h-40 w-40 rounded-bl-full bg-[#00FF88]/10 pointer-events-none" />
          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#00FF88] font-semibold">
                  Study Library
                </p>
                <Badge className="bg-[#00FF88]/15 text-[#00c96b] dark:text-[#00FF88] border-0 text-[10px] font-medium">
                  Instant Reader Active
                </Badge>
              </div>
              <h2 className="mt-1 font-outfit text-2xl font-bold text-gray-900 dark:text-white">
                Recordings & Notes
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-[#94A3B8]">
                Read class notes, PDFs, and watch replays directly inside your student dashboard without needing third-party apps.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.03] px-4 py-3">
                <p className="font-jetbrains text-2xl font-bold text-gray-900 dark:text-white">
                  {notes.length}
                </p>
                <p className="text-xs text-gray-500 dark:text-[#64748B]">Study Notes</p>
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.03] px-4 py-3">
                <p className="font-jetbrains text-2xl font-bold text-gray-900 dark:text-white">
                  {recordings.length}
                </p>
                <p className="text-xs text-gray-500 dark:text-[#64748B]">Class Recordings</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Tabs */}
        <Card className="border-gray-200 dark:border-white/[0.06] bg-white dark:bg-[#0B0D10]">
          <CardContent className="space-y-3 p-4">
            <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-[#64748B]" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search notes, topics, recordings..."
                  className="h-11 w-full rounded-lg border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.03] pl-10 pr-3 text-sm text-gray-900 dark:text-white outline-none transition placeholder:text-gray-400 dark:placeholder:text-[#52525B] focus:border-[#00FF88]"
                  data-testid="learning-materials-search"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setActiveTab("notes")}
                  className={`h-11 rounded-lg px-5 text-sm font-bold transition-all ${
                    activeTab === "notes"
                      ? "bg-[#00FF88] text-black shadow-sm"
                      : "bg-gray-100 dark:bg-white/[0.04] text-gray-600 dark:text-[#A1A1AA] hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-white/[0.08]"
                  }`}
                  data-testid="materials-notes-tab"
                >
                  Notes & Docs ({notes.length})
                </button>
                <button
                  onClick={() => setActiveTab("recordings")}
                  className={`h-11 rounded-lg px-5 text-sm font-bold transition-all ${
                    activeTab === "recordings"
                      ? "bg-[#00FF88] text-black shadow-sm"
                      : "bg-gray-100 dark:bg-white/[0.04] text-gray-600 dark:text-[#A1A1AA] hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-white/[0.08]"
                  }`}
                  data-testid="materials-recordings-tab"
                >
                  Recordings ({recordings.length})
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes Tab (with In-Dashboard Viewer) */}
        {activeTab === "notes" &&
          (filteredNotes.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No notes found"
              subtitle="Lecture notes, PDFs, and study materials will appear here once uploaded."
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {filteredNotes.map((note) => (
                <Card
                  key={note.note_id}
                  onClick={() => setSelectedNote(note)}
                  className="group relative cursor-pointer border-gray-200 dark:border-white/[0.08] bg-white dark:bg-[#0B0D10] transition-all hover:-translate-y-1 hover:border-[#00FF88]/50 hover:shadow-lg dark:hover:shadow-emerald-950/20"
                  data-testid={`note-card-${note.note_id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      {/* Document Icon */}
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-[#00c96b] dark:text-[#00FF88] group-hover:scale-105 transition-transform">
                        <FileText className="h-6 w-6" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="truncate font-outfit text-base font-semibold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-[#00FF88] transition-colors">
                            {note.title}
                          </h3>
                          {(!note.course_id || note.is_all_students) && (
                            <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-[10px] px-2 py-0.5">
                              All Students
                            </Badge>
                          )}
                        </div>

                        {note.description && (
                          <p className="line-clamp-2 text-sm text-gray-500 dark:text-[#94A3B8]">
                            {note.description}
                          </p>
                        )}

                        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-400 dark:text-[#64748B]">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {note.created_at
                              ? new Date(note.created_at).toLocaleDateString()
                              : "Recently"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Download className="h-3 w-3" />
                            {note.download_count || 0} reads & downloads
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Bar */}
                    <div className="mt-4 pt-3.5 border-t border-gray-100 dark:border-white/[0.06] flex items-center justify-between gap-2">
                      <div className="text-xs text-emerald-600 dark:text-[#00FF88] font-medium flex items-center gap-1">
                        <Sparkles className="w-3.5 h-3.5" />
                        Click to read on dashboard
                      </div>

                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          onClick={() => setSelectedNote(note)}
                          className="bg-[#00FF88] text-black hover:bg-[#20FF99] font-bold text-xs h-9 px-4 rounded-lg flex items-center gap-1.5 shadow-xs"
                          data-testid={`view-note-${note.note_id}`}
                        >
                          <Eye className="w-4 h-4" />
                          Read Note
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadNote(note)}
                          className="border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 h-9 px-3 rounded-lg"
                          title="Download offline copy"
                          data-testid={`download-note-${note.note_id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ))}

        {/* Recordings Tab */}
        {activeTab === "recordings" &&
          (filteredRecordings.length === 0 ? (
            <EmptyState
              icon={PlayCircle}
              title="No recordings found"
              subtitle="Class recordings will appear here after upload."
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredRecordings.map((recording) => (
                <Card
                  key={recording.recording_id}
                  className="group overflow-hidden border-gray-200 dark:border-white/[0.06] bg-white dark:bg-[#0B0D10] transition-all hover:-translate-y-1 hover:border-[#00FF88]/25"
                  data-testid={`recording-card-${recording.recording_id}`}
                >
                  <button
                    className="block w-full text-left"
                    onClick={() =>
                      recording.video_url &&
                      window.open(recording.video_url, "_blank", "noopener,noreferrer")
                    }
                  >
                    <div className="relative aspect-video bg-gray-50 dark:bg-white/[0.03]">
                      <img
                        src={
                          recording.thumbnail ||
                          getYouTubeThumbnail(recording.video_url) ||
                          "https://images.pexels.com/photos/6958520/pexels-photo-6958520.jpeg?w=500"
                        }
                        alt={recording.title}
                        className="h-full w-full object-cover opacity-90 transition group-hover:scale-[1.02]"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/35 opacity-0 transition group-hover:opacity-100">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#00FF88]">
                          <PlayCircle className="h-7 w-7 text-black" />
                        </div>
                      </div>
                      {recording.duration_minutes && (
                        <Badge className="absolute bottom-2 right-2 border-0 bg-black/80 text-gray-900 dark:text-white">
                          <Clock className="mr-1 h-3 w-3" /> {recording.duration_minutes} min
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="line-clamp-1 font-outfit text-base font-semibold text-gray-900 dark:text-white">
                        {recording.title}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-[#64748B]">
                        {recording.description}
                      </p>
                      <p className="mt-3 flex items-center gap-1 text-xs text-gray-500 dark:text-[#64748B]">
                        <Calendar className="h-3 w-3" />{" "}
                        {recording.created_at
                          ? new Date(recording.created_at).toLocaleDateString()
                          : "Recently"}
                      </p>
                    </CardContent>
                  </button>
                </Card>
              ))}
            </div>
          ))}

        {/* ===================== IN-DASHBOARD DOCUMENT VIEWER MODAL ===================== */}
        {selectedNote && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4 animate-in fade-in duration-200"
            onClick={() => setSelectedNote(null)}
          >
            <div
              className={`relative flex flex-col bg-white dark:bg-[#0D1117] rounded-2xl border border-gray-200 dark:border-white/10 shadow-2xl transition-all overflow-hidden ${
                fullscreen
                  ? "w-full h-full max-w-none rounded-none"
                  : "w-full max-w-5xl h-[88vh] max-h-[920px]"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Reader Topbar */}
              <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-200 dark:border-white/10 bg-gray-50/80 dark:bg-white/[0.02]">
                <div className="flex items-center gap-3 min-w-0 pr-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-[#00c96b] dark:text-[#00FF88] flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="font-outfit text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate">
                        {selectedNote.title}
                      </h2>
                      {(!selectedNote.course_id || selectedNote.is_all_students) && (
                        <span className="hidden sm:inline-block text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                          All Students
                        </span>
                      )}
                    </div>
                    {selectedNote.description && (
                      <p className="text-xs text-gray-500 dark:text-[#94A3B8] truncate hidden sm:block">
                        {selectedNote.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Reader Controls */}
                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadNote(selectedNote)}
                    className="border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 h-9 px-3 rounded-lg text-xs flex items-center gap-1.5"
                    title="Download offline copy"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">Download</span>
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      window.open(selectedNote.file_url, "_blank", "noopener,noreferrer")
                    }
                    className="border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 h-9 px-3 rounded-lg text-xs flex items-center gap-1.5"
                    title="Open in new browser tab"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">New Tab</span>
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setFullscreen(!fullscreen)}
                    className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white h-9 w-9 p-0 rounded-lg"
                    title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}
                  >
                    {fullscreen ? (
                      <Minimize2 className="w-4 h-4" />
                    ) : (
                      <Maximize2 className="w-4 h-4" />
                    )}
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedNote(null)}
                    className="text-gray-500 hover:text-rose-600 dark:text-gray-400 dark:hover:text-rose-400 h-9 w-9 p-0 rounded-lg"
                    title="Close Reader"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Reader Document Display Frame */}
              <div className="flex-1 w-full h-full bg-[#1e1e1e] relative overflow-hidden">
                {isImageFile(selectedNote.file_url) ? (
                  <div className="w-full h-full flex items-center justify-center p-4 overflow-auto bg-[#0a0a0a]">
                    <img
                      src={selectedNote.file_url}
                      alt={selectedNote.title}
                      className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
                    />
                  </div>
                ) : (
                  <iframe
                    src={getViewerUrl(selectedNote.file_url)}
                    title={selectedNote.title}
                    className="w-full h-full border-0 bg-white"
                    allow="autoplay"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

const EmptyState = ({ icon: Icon, title, subtitle }) => (
  <Card className="border-gray-200 dark:border-white/[0.06] bg-white dark:bg-[#0B0D10]">
    <CardContent className="p-12 text-center">
      <Icon className="mx-auto mb-4 h-12 w-12 text-gray-400 dark:text-[#52525B]" />
      <p className="font-outfit text-lg font-semibold text-gray-900 dark:text-white">
        {title}
      </p>
      <p className="mt-1 text-sm text-gray-500 dark:text-[#64748B]">{subtitle}</p>
    </CardContent>
  </Card>
);

export default LearningMaterialsPage;
